/**
 * Setting up the first bank account.
 *
 * THE DEAD END THIS REPLACES
 * --------------------------
 * The reconciliation screen said "No bank account has been set up yet" and
 * stopped there. Nothing in the application could set one up — every banking
 * route hangs off an account id, and no route created one. So in a fresh
 * installation, which from launch day is every installation, the whole of
 * banking was unreachable and the screen gave no hint that anything was wrong.
 *
 * WHY IT ASKS WHICH LEDGER ACCOUNT
 * --------------------------------
 * A bank account here is a real account at a real bank tied to one account in
 * the books. The tie is what makes reconciliation mean anything: the books side
 * of the comparison is the journal lines on that ledger account. Choosing it is
 * an accounting decision, so it is asked rather than guessed — but the list is
 * narrowed to the Cash & Bank accounts, so the wrong kind cannot be picked.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Field } from '../../components/ui/Field.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

export function AddBankAccount({ onDone, onCancel }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    bankName: '',
    accountNumber: '',
    accountTitle: '',
    accountId: '',
    iban: '',
  });

  const { data: ledger } = useQuery({
    queryKey: ['banking', 'ledger-accounts'],
    queryFn: () => api.get('/banking/ledger-accounts'),
  });

  const free = (ledger?.accounts ?? []).filter((a) => !a.taken);

  const save = useMutation({
    mutationFn: (body) => api.post('/banking/accounts', body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['banking'] });
      onDone?.(created.account);
    },
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // The ledger account is picked by the person, never defaulted. Defaulting it
  // would mean an accounting decision made silently by whoever tabbed past.
  const ready = form.bankName.trim() && form.accountNumber.trim() && form.accountId;

  if (!ledger) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Spinner label="Loading" />
      </div>
    );
  }

  return (
    <form
      className="mx-auto max-w-xl rounded-xl border border-rule bg-surface-2 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) save.mutate(form);
      }}
    >
      <h2 className="font-semibold text-ink text-lg">Add a bank account</h2>
      <p className="mt-1 text-ink-2 text-sm">
        The account the restaurant banks with. Once it is here, statements can be
        imported and set against the books.
      </p>

      {free.length === 0 ? (
        <p className="mt-4 rounded-lg border border-rule bg-surface-3 p-3 text-ink-2 text-sm">
          Every Cash &amp; Bank account in the chart of accounts already stands
          for a bank account. Add a new one to the chart of accounts first.
        </p>
      ) : (
        <div className="mt-4 grid gap-3">
          <Field
            label="Bank"
            value={form.bankName}
            onChange={set('bankName')}
            placeholder="HBL"
            autoFocus
          />
          <Field
            label="Account number"
            value={form.accountNumber}
            onChange={set('accountNumber')}
            placeholder="0123456789"
          />
          <Field
            label="Account title"
            hint="The name the account is held in. Optional."
            value={form.accountTitle}
            onChange={set('accountTitle')}
          />
          <Field
            label="IBAN"
            hint="Optional."
            value={form.iban}
            onChange={set('iban')}
            placeholder="PK36SCBL0000001123456702"
          />

          <label>
            <span className="mb-1.5 block font-medium text-ink-2 text-sm">
              Which account in the books
            </span>
            <select
              value={form.accountId}
              onChange={set('accountId')}
              className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-ink text-sm focus:border-brand-400 focus:outline-none"
            >
              <option value="">Choose one</option>
              {free.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
            <span className="mt-1.5 block text-ink-3 text-xs">
              Money in and out of this bank account is recorded here.
            </span>
          </label>
        </div>
      )}

      {save.isError && (
        <p role="alert" className="mt-3 text-bad text-sm">
          {save.error?.message || 'Could not save the account.'}
        </p>
      )}

      <div className="mt-5 flex gap-2">
        <Button type="submit" disabled={!ready || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save account'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
